import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add,
  CalendarToday,
  ViewWeek,
  ViewDay,
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths, startOfWeek, endOfWeek, isToday, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom'; // Import for navigation

// Import RTK Query hooks
import { 
  useListWorkoutsQuery, 
  useCreateWorkoutMutation, 
  useUpdateWorkoutMutation,
  useGetSimpleWorkoutCoursesQuery // NEW: Import the simple courses hook
} from '../../../store/api/baseApi';
import type { WorkoutResponse, WorkoutStatus, WorkoutPriority, WorkoutCourseSimpleResponse } from '../../../store/api/models/workoutModels';

type ViewMode = 'month' | 'week' | 'day';

const WorkoutCalendarPage: React.FC = () => {
  const navigate = useNavigate(); // Hook for navigation
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isAddWorkoutDialogOpen, setIsAddWorkoutDialogOpen] = useState(false);
  
  // API Hooks
  const { data: workouts = [], isLoading, refetch } = useListWorkoutsQuery({});
  const [createWorkout, { isLoading: isCreating }] = useCreateWorkoutMutation();
  const [updateWorkout, { isLoading: isUpdating }] = useUpdateWorkoutMutation();
  
  // NEW: Fetch simple list of courses for the dropdown
  const { data: courses = [], isLoading: isLoadingCourses } = useGetSimpleWorkoutCoursesQuery();

  const [filterSportType, setFilterSportType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: 'success' | 'error' }>({ 
    open: false, 
    message: '',
    severity: 'success'
  });

  // Updated Form State: Name is derived, Course ID is stored
  const [newWorkout, setNewWorkout] = useState<{
    course_id: number | null; 
    sport_type: 'run' | 'ride' | 'swim';
    priority: WorkoutPriority;
    notes: string;
    scheduled_at: string;
  }>({
    course_id: null,
    sport_type: 'run',
    priority: 'medium',
    notes: '',
    scheduled_at: '',
  });

  const handlePreviousPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000));
    }
  };

  const handleNextPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setNewWorkout(prev => ({ 
      ...prev, 
      scheduled_at: format(date, "yyyy-MM-dd'T'HH:mm"),
      course_id: null, // Reset course selection
      sport_type: 'run' // Reset default sport
    }));
    setIsAddWorkoutDialogOpen(true);
  };

  // Updated: Logic to derive name from course and create workout
  const handleAddWorkout = async () => {
    if (!newWorkout.course_id || !newWorkout.scheduled_at) return;

    // Find the selected course object to get the name
    const selectedCourse = courses.find(c => c.id === newWorkout.course_id);
    if (!selectedCourse) return;

    try {
      await createWorkout({
        ...newWorkout,
        name: selectedCourse.name, // Name comes from course, user cannot edit
        course_id: newWorkout.course_id,
        sport_type: selectedCourse.sport_type, // Ensure sport type matches course
      }).unwrap();
      
      setSnackbar({ open: true, message: 'Workout created successfully!', severity: 'success' });
      setIsAddWorkoutDialogOpen(false);
      setNewWorkout({
        course_id: null,
        sport_type: 'run',
        priority: 'medium',
        notes: '',
        scheduled_at: ''
      });
      refetch();
    } catch (error) {
      console.error('Failed to create workout:', error);
      setSnackbar({ open: true, message: 'Failed to create workout', severity: 'error' });
    }
  };

  // Helper to handle course selection change
  const handleCourseChange = (courseId: number) => {
    const selectedCourse = courses.find(c => c.id === courseId);
    setNewWorkout(prev => ({
      ...prev,
      course_id: courseId,
      sport_type: selectedCourse?.sport_type || 'run' // Auto-update sport type based on course
    }));
  };

  const handleStatusChange = async (workoutId: number, newStatus: WorkoutStatus) => {
    try {
      await updateWorkout({ 
        id: workoutId.toString(), 
        data: { status: newStatus } 
      }).unwrap();
      
      refetch();
      setSnackbar({ open: true, message: `Workout marked as ${newStatus}`, severity: 'success' });
    } catch (error) {
      console.error('Failed to update workout:', error);
      setSnackbar({ open: true, message: 'Failed to update workout', severity: 'error' });
    }
  };

  const getWorkoutsForDate = (date: Date): WorkoutResponse[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return workouts.filter((workout) => {
      if (!workout.scheduled_at) return false;
      const wDate = format(parseISO(workout.scheduled_at), 'yyyy-MM-dd');
      const matchesDate = wDate === dateStr;
      
      const matchesSport = filterSportType === 'all';
      const matchesStatus = filterStatus === 'all' || workout.status === filterStatus;
      return matchesDate && matchesSport && matchesStatus;
    });
  };

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [currentDate]);

  const getStatusColor = (status: WorkoutStatus): string => {
    switch (status) {
      case 'completed':
        return 'success.main';
      case 'cancelled':
        return 'warning.main';
      case 'in_progress':
        return 'info.main';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string | null | undefined): string => {
    switch (priority) {
      case 'high':
        return 'error.main';
      case 'medium':
        return 'warning.main';
      case 'low':
        return 'success.main';
      default:
        return 'warning.main'; // Default to medium if null/undefined
    }
  };

  const renderMonthView = () => (
    <Grid container spacing={0} sx={{ border: 1, borderColor: 'divider' }}>
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
        <Grid item xs={12 / 7} key={day}>
          <Box
            sx={{
              p: 1,
              textAlign: 'center',
              fontWeight: 'bold',
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'action.hover',
            }}
          >
            {day}
          </Box>
        </Grid>
      ))}
      {calendarDays.map((day, index) => {
        const dayWorkouts = getWorkoutsForDate(day);
        const isCurrentMonth = isSameMonth(day, currentDate);
        const isTodayDate = isToday(day);

        return (
          <Grid
            item
            xs={12 / 7}
            key={index}
            sx={{
              minHeight: 120,
              borderRight: index % 7 !== 6 ? 1 : 0,
              borderBottom: index < calendarDays.length - 7 ? 1 : 0,
              borderColor: 'divider',
              backgroundColor: isCurrentMonth ? 'background.paper' : 'action.hover',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.selected',
              },
            }}
            onClick={() => handleDateClick(day)}
          >
            <Box sx={{ p: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isTodayDate ? 'bold' : 'normal',
                  color: isTodayDate ? 'primary.main' : isCurrentMonth ? 'text.primary' : 'text.disabled',
                }}
              >
                {format(day, 'd')}
              </Typography>
              {dayWorkouts.slice(0, 2).map((workout) => (
                <Chip
                  key={workout.id}
                  label={workout.name}
                  size="small"
                  sx={{
                    mt: 0.5,
                    width: '100%',
                    fontSize: '0.7rem',
                    height: 20,
                    bgcolor: getStatusColor(workout.status),
                    color: 'white',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                />
              ))}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderWeekView = () => (
    <Grid container spacing={1}>
      {weekDays.map((day) => {
        const dayWorkouts = getWorkoutsForDate(day);
        const isTodayDate = isToday(day);

        return (
          <Grid item xs={12 / 7} key={day.toISOString()}>
            <Paper
              sx={{
                p: 2,
                minHeight: 400,
                backgroundColor: isTodayDate ? 'primary.light' : 'background.paper',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: isTodayDate ? 'primary.light' : 'action.hover',
                },
              }}
              onClick={() => handleDateClick(day)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {format(day, 'EEE')}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {format(day, 'd')}
              </Typography>
              {dayWorkouts.map((workout) => (
                <Card
                  key={workout.id}
                  sx={{
                    mb: 1,
                    borderLeft: 4,
                    borderColor: getPriorityColor(workout.priority || 'medium'),
                  }}
                >
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {workout.name}
                    </Typography>
                    <Chip
                      label={workout.status}
                      size="small"
                      sx={{
                        mt: 0.5,
                        fontSize: '0.7rem',
                        height: 18,
                        bgcolor: getStatusColor(workout.status),
                        color: 'white',
                      }}
                    />
                  </CardContent>
                </Card>
              ))}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderDayView = () => {
    const dayWorkouts = getWorkoutsForDate(currentDate);

    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
          {format(currentDate, 'EEEE, MMMM d, yyyy')}
        </Typography>
        {dayWorkouts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No workouts scheduled for this day
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ mt: 2 }}
              onClick={() => handleDateClick(currentDate)}
            >
              Add Workout
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {dayWorkouts.map((workout) => (
              <Grid item xs={12} md={6} key={workout.id}>
                <Card sx={{ borderLeft: 4, borderColor: getPriorityColor(workout.priority || 'medium') }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {workout.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={workout.status}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(workout.status),
                          color: 'white',
                        }}
                      />
                      <Chip label={workout.priority} size="small" variant="outlined" />
                    </Box>
                    {workout.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {workout.notes}
                      </Typography>
                    )}
                    {workout.status === 'planned' && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button 
                          size="small" 
                          variant="contained"
                          onClick={() => handleStatusChange(workout.id, 'completed')}
                          disabled={isUpdating}
                        >
                          Complete
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleDateClick(currentDate)}
                        >
                          Edit
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Workout Calendar
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sport Type</InputLabel>
            <Select value={filterSportType} onChange={(e) => setFilterSportType(e.target.value)} label="Sport Type">
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="run">Run</MenuItem>
              <MenuItem value="ride">Ride</MenuItem>
              <MenuItem value="swim">Swim</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="planned">Planned</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePreviousPeriod}>
              <ChevronLeft />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
              {viewMode === 'month' && format(currentDate, 'MMMM yyyy')}
              {viewMode === 'week' && `Week of ${format(startOfWeek(currentDate), 'MMM d, yyyy')}`}
              {viewMode === 'day' && format(currentDate, 'MMMM d, yyyy')}
            </Typography>
            <IconButton onClick={handleNextPeriod}>
              <ChevronRight />
            </IconButton>
            <Button variant="outlined" size="small" onClick={handleToday}>
              Today
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ButtonGroup variant="outlined" size="small">
              <Button
                variant={viewMode === 'month' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('month')}
                startIcon={<CalendarToday />}
              >
                Month
              </Button>
              <Button
                variant={viewMode === 'week' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('week')}
                startIcon={<ViewWeek />}
              >
                Week
              </Button>
              <Button
                variant={viewMode === 'day' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('day')}
                startIcon={<ViewDay />}
              >
                Day
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'day' && renderDayView()}
        </>
      )}

      <Dialog open={isAddWorkoutDialogOpen} onClose={() => setIsAddWorkoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Workout for {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            
            {/* Course Selection */}
            <FormControl fullWidth disabled={isLoadingCourses}>
              <InputLabel>Select Course</InputLabel>
              <Select
                value={newWorkout.course_id || ''}
                onChange={(e) => handleCourseChange(Number(e.target.value))}
                label="Select Course"
              >
                {courses.map((course) => (
                  <MenuItem key={course.id} value={course.id}>
                    {course.name} ({course.sport_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* View Workout Button */}
            {newWorkout.course_id && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/trainer/templates/edit/${newWorkout.course_id}`)}
                sx={{ mt: -1 }}
              >
                View Workout
              </Button>
            )}

            <FormControl fullWidth>
              <InputLabel>Sport Type</InputLabel>
              <Select
                value={newWorkout.sport_type}
                onChange={(e) => setNewWorkout({ ...newWorkout, sport_type: e.target.value as 'run' | 'ride' | 'swim' })}
                label="Sport Type"
              >
                <MenuItem value="run">Run</MenuItem>
                <MenuItem value="ride">Ride</MenuItem>
                <MenuItem value="swim">Swim</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={newWorkout.priority}
                onChange={(e) => setNewWorkout({ ...newWorkout, priority: e.target.value as WorkoutPriority })}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Scheduled At"
              type="datetime-local"
              fullWidth
              value={newWorkout.scheduled_at}
              onChange={(e) => setNewWorkout({ ...newWorkout, scheduled_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={newWorkout.notes}
              onChange={(e) => setNewWorkout({ ...newWorkout, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddWorkoutDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddWorkout} 
            variant="contained" 
            disabled={!newWorkout.course_id || isCreating}
          >
            {isCreating ? 'Adding...' : 'Add Workout'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <MuiAlert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default WorkoutCalendarPage;